import { AlertCircle, CheckCircle2, Loader2, Plus, Sparkles, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { adminClient } from '@/api/adminClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import {
  CATEGORIES,
  CORE_CAPABILITIES,
  capabilityProblem,
  copyFor,
  normalizeKeyword,
  promptMatchesKeywords,
  sanitizeCapabilityInput,
  type StepProps,
} from '../builderModel';
import { Field, IntegrationCardPreview, StepFrame } from '../builderUi';
import { LogoDesigner } from '../LogoDesigner';

export function IdentityStep({ draft, readOnly, saving, save, replace, next }: StepProps) {
  const [capability, setCapability] = useState(draft.capability);
  const [category, setCategory] = useState(draft.category);
  const [brandColor, setBrandColor] = useState(draft.brandColor);
  const [logoDataUrl, setLogoDataUrl] = useState(draft.logoDataUrl ?? '');
  const [poweredBy, setPoweredBy] = useState(draft.poweredBy.join(', '));
  const [keywords, setKeywords] = useState<string[]>(draft.keywords ?? []);
  const [newKeyword, setNewKeyword] = useState('');
  const [trialPrompt, setTrialPrompt] = useState('');
  const [savingBrand, setSavingBrand] = useState(false);
  const live = draft.status === 'published';
  const brandDirty = brandColor !== draft.brandColor || logoDataUrl !== (draft.logoDataUrl ?? '');

  // The logo and colour stay editable when the rest is locked. On a live integration the RapidSOS team sees a change at once; customers get it with the next release.
  const saveBrand = async () => {
    setSavingBrand(true);
    try {
      replace(await adminClient.updateIntegrationDraftBrand(draft.draftId, { brandColor, logoDataUrl }));
      toast.success(live ? 'Logo saved for the RapidSOS team. Release it on the Review step to show customers.' : 'Logo saved');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save the logo');
    } finally {
      setSavingBrand(false);
    }
  };

  const poweredByList = poweredBy
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
  const problem = capability ? capabilityProblem(capability) : undefined;
  const dirty =
    capability !== draft.capability ||
    category !== draft.category ||
    brandColor !== draft.brandColor ||
    logoDataUrl !== (draft.logoDataUrl ?? '') ||
    poweredByList.join(',') !== draft.poweredBy.join(',') ||
    keywords.join('|') !== (draft.keywords ?? []).join('|');

  const persist = () => {
    if (problem) {
      toast.error(problem);
      return Promise.resolve(undefined);
    }
    return save({ capability, category, brandColor, logoDataUrl, poweredBy: poweredByList, keywords });
  };

  const addKeyword = () => {
    const keyword = normalizeKeyword(newKeyword);
    if (!keyword) {
      toast.error('Keywords are 3–60 characters of letters, digits and spaces.');
      return;
    }
    if (!keywords.includes(keyword)) setKeywords([...keywords, keyword].slice(0, 20));
    setNewKeyword('');
  };

  const copy = copyFor(draft, 'en');
  const trialMatches = trialPrompt.trim() ? promptMatchesKeywords(trialPrompt, keywords) : undefined;

  return (
    <StepFrame
      title="Identity & brand"
      description="Name the capability generated apps will ask for, how the integration looks in the catalog, and the words that should make Studio suggest it."
      dirty={dirty}
      saving={saving}
      readOnly={readOnly}
      onSave={() => void persist()}
      onContinue={async () => {
        if (!dirty || (await persist())) next();
      }}
      aside={
        <div className="sticky top-6">
          <IntegrationCardPreview copy={copy} category={category} brandColor={brandColor} logoDataUrl={logoDataUrl || undefined} pricing={draft.pricing} />
        </div>
      }
    >
      <div className="grid gap-6 sm:grid-cols-2">
        <Field id="integration-id" label="Integration id" hint="Permanent. It names the vault path and the catalog entry.">
          <Input id="integration-id" value={draft.integrationId} disabled className="font-mono text-sm" />
        </Field>
        <Field
          id="capability"
          label="Capability"
          hint={
            <>
              Two or three lowercase parts joined by dots. App code calls{' '}
              <code className="font-mono">ctx.integrations.require('{capability || 'acme.directory'}')</code>.
            </>
          }
        >
          <div className="relative">
            <Input
              id="capability"
              value={capability}
              disabled={readOnly}
              onChange={(event) => setCapability(sanitizeCapabilityInput(event.target.value))}
              placeholder="acme.directory"
              aria-invalid={Boolean(problem)}
              className={cn('pr-9 font-mono text-sm', problem && 'border-destructive focus-visible:ring-destructive')}
            />
            {capability ? (
              problem ? (
                <AlertCircle className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-destructive" />
              ) : (
                <CheckCircle2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-success" />
              )
            ) : null}
          </div>
          {problem ? <p className="text-xs text-destructive">{problem}</p> : null}
          {!readOnly && draft.capabilitySuggestions.length ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <Sparkles className="h-3 w-3 text-muted-foreground" />
              {draft.capabilitySuggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => setCapability(suggestion)}
                  className="rounded-md border border-border px-2 py-0.5 font-mono text-[11px] text-muted-foreground transition-colors hover:border-brand/40 hover:text-foreground"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          ) : null}
        </Field>
      </div>
      <p className="-mt-3 text-[11px] leading-5 text-muted-foreground">
        Reserved for SDK adapters: {CORE_CAPABILITIES.map((item) => <code key={item} className="mr-1 font-mono">{item}</code>)}
      </p>

      <Field label="Category">
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((item) => (
            <button
              key={item}
              type="button"
              disabled={readOnly}
              onClick={() => setCategory(item)}
              className={cn(
                'rounded-full border px-3 py-1 text-xs transition-colors',
                category === item ? 'border-brand bg-brand-muted text-foreground' : 'border-border text-muted-foreground hover:text-foreground',
              )}
            >
              {item}
            </button>
          ))}
        </div>
      </Field>

      <div className="rounded-xl border border-border bg-card/40 p-5">
        <div className="text-sm font-semibold">Discovery keywords</div>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          When a builder&apos;s prompt contains one of these phrases and Studio confirms it fits what the app is for, Studio suggests
          this integration; the builder decides whether to pin it. Use phrases only this integration answers, like “responder
          directory” or the product&apos;s name, not an everyday word such as “transcript” or “projects”. Matching is whole words,
          case-insensitive.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {keywords.map((keyword) => (
            <span key={keyword} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 text-xs">
              {keyword}
              {readOnly ? null : (
                <button type="button" aria-label={`Remove ${keyword}`} onClick={() => setKeywords(keywords.filter((item) => item !== keyword))}>
                  <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                </button>
              )}
            </span>
          ))}
          {keywords.length === 0 ? <span className="text-xs text-muted-foreground">No keywords yet — builders can still pin it by hand.</span> : null}
        </div>
        {readOnly ? null : (
          <div className="mt-3 flex gap-2">
            <Input
              value={newKeyword}
              onChange={(event) => setNewKeyword(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addKeyword();
                }
              }}
              placeholder="responder directory"
              aria-label="New keyword"
            />
            <Button variant="outline" onClick={addKeyword} disabled={!newKeyword.trim()} aria-label="Add keyword">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        )}
        <div className="mt-4 border-t border-border pt-4">
          <Field id="trial-prompt" label="Try a prompt">
            <Input
              id="trial-prompt"
              value={trialPrompt}
              onChange={(event) => setTrialPrompt(event.target.value)}
              placeholder="Build a dispatch board that pulls from the responder directory"
            />
          </Field>
          {trialMatches === undefined ? null : (
            <p className={cn('mt-2 text-xs', trialMatches ? 'text-success' : 'text-muted-foreground')}>
              {trialMatches ? 'This prompt matches; Studio would suggest it if it fits the app.' : 'No keyword matches this prompt.'}
            </p>
          )}
        </div>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <Field id="brand-color" label="Brand colour" hint="Accent stripe on the catalog card, and the main colour of a generated logo.">
          <div className="flex items-center gap-2">
            <input
              type="color"
              aria-label="Pick brand colour"
              value={/^#[0-9a-fA-F]{6}$/.test(brandColor) ? brandColor : '#18181b'}
              onChange={(event) => setBrandColor(event.target.value)}
              className="h-10 w-12 cursor-pointer rounded-md border border-input bg-background p-1"
            />
            <Input id="brand-color" value={brandColor} onChange={(event) => setBrandColor(event.target.value)} className="font-mono text-sm" />
          </div>
        </Field>
        <Field id="powered-by" label="Powered by" optional hint="The vendor or service behind it, comma separated.">
          <Input id="powered-by" value={poweredBy} disabled={readOnly} onChange={(event) => setPoweredBy(event.target.value)} placeholder="Acme Directory Service" />
        </Field>
      </div>

      <LogoDesigner
        draftId={draft.draftId}
        title={copy.title || draft.integrationId}
        brandColor={brandColor}
        category={category}
        poweredBy={poweredByList}
        value={logoDataUrl}
        onChange={setLogoDataUrl}
        readOnly={false}
      />
      {readOnly ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card/40 px-4 py-3">
          <p className="text-xs text-muted-foreground">
            {live
              ? 'The rest is locked while it is live, but the logo and colour can change. The RapidSOS team sees a change right away; customers get it when you release it.'
              : 'The rest is locked for review, but the logo and colour can still change.'}
          </p>
          <Button onClick={() => void saveBrand()} disabled={!brandDirty || savingBrand}>
            {savingBrand ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save logo and colour
          </Button>
        </div>
      ) : null}

      <p className="text-xs leading-5 text-muted-foreground">
        Connectors are organization-wide: RapidSOS holds one set of credentials and every organization that turns the integration on
        shares it. Per-user accounts (like a personal inbox) need an SDK adapter with OAuth.
      </p>
    </StepFrame>
  );
}
